import type {
  TCompanyReadinessReviewData,
  TFundraiserSummaryReview,
  TFundraiserSummaryReviewData,
  TInvestorReadinessReview,
  TInvestorSummaryReview,
  TInvestorSummaryReviewData,
  TReviewFormValues,
  TReviewerCompany,
  TReviewerDocument,
} from "@/api/reviewer";
import { reviewerService } from "@/api/reviewer";
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Textarea, useToast } from "@/components/ui";
import { Input } from "@/components/ui/Input";
import { extractErrorMessage, triggerBlobDownload } from "@/utils";
import { Download, FileText, Loader2, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type TReviewKind = "investor-readiness" | "fundraiser-summary" | "investor-summary";
type TSourceRecord = TCompanyReadinessReviewData | TFundraiserSummaryReviewData | TInvestorSummaryReviewData;
type TReviewRecord = TInvestorReadinessReview | TFundraiserSummaryReview | TInvestorSummaryReview;
type TJsonObject = Record<string, unknown>;

const REVIEW_TYPES: { id: TReviewKind; label: string; description: string }[] = [
  {
    id: "investor-readiness",
    label: "Investor Readiness",
    description: "AI readiness analysis for the fundraiser company",
  },
  {
    id: "fundraiser-summary",
    label: "Fundraiser Summary",
    description: "Fundraiser summary shown to investors",
  },
  {
    id: "investor-summary",
    label: "Investor Summary",
    description: "Investor summary shown to fundraisers",
  },
];

function formatDate(date?: string) {
  if (!date) return "Unknown date";
  try {
    return new Date(date).toLocaleString();
  } catch {
    return date;
  }
}

function formatFileSize(bytes?: number | null) {
  if (!bytes || bytes <= 0) return "Unknown size";
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatFileType(fileType?: string | null) {
  if (!fileType) return "Document";
  return fileType
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function parseJsonObject(value: string) {
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as TJsonObject) : null;
  } catch {
    return null;
  }
}

function asObject(value: unknown) {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as TJsonObject) : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? (value as unknown[]) : [];
}

function asString(value: unknown) {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

function formatNumber(value: unknown, digits = 1) {
  return typeof value === "number" && Number.isFinite(value) ? value.toFixed(digits) : "N/A";
}

function splitTaskContent(content: string) {
  const parts = content.split("|").map((part) => part.trim());
  return {
    title: parts[0] || content,
    description: parts[1] || "",
    outcome: parts[2] || "",
  };
}

function getSourceId(kind: TReviewKind, source: TSourceRecord) {
  if (kind === "investor-readiness") return (source as TCompanyReadinessReviewData).id;
  if (kind === "fundraiser-summary") {
    return (source as TFundraiserSummaryReviewData).fundraiser_summary_for_investors_id;
  }
  return (source as TInvestorSummaryReviewData).investor_summary_for_fundraisers_id;
}

function getSourceDate(source: TSourceRecord | null) {
  if (!source) return undefined;
  return "created_at" in source ? source.created_at : undefined;
}

function getSourceSummary(kind: TReviewKind, source: TSourceRecord | null) {
  if (!source) return "";
  if (kind === "investor-readiness") {
    return (source as TCompanyReadinessReviewData).readiness_summary ?? "";
  }
  return (source as TFundraiserSummaryReviewData | TInvestorSummaryReviewData).summary_text ?? "";
}

function getSourceJson(kind: TReviewKind, source: TSourceRecord | null) {
  if (!source) return null;
  if (kind === "investor-readiness") {
    return (source as TCompanyReadinessReviewData).readiness_data ?? null;
  }
  return (source as TFundraiserSummaryReviewData | TInvestorSummaryReviewData).summary_json ?? null;
}

function getReviewSummary(review: TReviewRecord | null) {
  if (!review) return "";
  const summaryFields = review as { reviewer_summary?: string | null; reviewers_summary?: string | null };
  return summaryFields.reviewer_summary ?? summaryFields.reviewers_summary ?? "";
}

const emptyForm: TReviewFormValues = {
  positive_critique_of_ai: "",
  negative_critique_of_ai: "",
  reviewer_summary: "",
};

function TextList({ items, emptyLabel }: { items: unknown[]; emptyLabel: string }) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  }

  return (
    <ul className="space-y-2">
      {items.map((item, index) => (
        <li
          key={`${asString(item)}-${index}`}
          className="rounded-md border border-border bg-card p-2 text-sm leading-5 text-foreground"
        >
          {asString(item) || JSON.stringify(item)}
        </li>
      ))}
    </ul>
  );
}

function AssessmentBlock({ title, assessment }: { title: string; assessment: TJsonObject | null }) {
  if (!assessment) return null;

  const evidence = asArray(assessment.evidence);
  const gaps = asArray(assessment.gaps);
  const todo = asArray(assessment.todo);

  return (
    <div className="space-y-3 rounded-md border border-border bg-background p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold text-foreground">{title}</h4>
        {assessment.level !== undefined && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
            {asString(assessment.level)}
          </span>
        )}
        {assessment.score !== undefined && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
            Score {formatNumber(assessment.score, 0)}
          </span>
        )}
        {assessment.confidence !== undefined && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {asString(assessment.confidence)} confidence
          </span>
        )}
      </div>

      {assessment.score_explanation !== undefined && (
        <p className="whitespace-pre-wrap text-xs leading-5 text-muted-foreground">
          {asString(assessment.score_explanation)}
        </p>
      )}
      {assessment.reasoning !== undefined && (
        <p className="text-sm leading-6 text-foreground">{asString(assessment.reasoning)}</p>
      )}

      <div className="grid gap-3 lg:grid-cols-3">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Evidence</p>
          <TextList
            items={evidence}
            emptyLabel="No evidence listed."
          />
        </div>
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Gaps</p>
          <TextList
            items={gaps}
            emptyLabel="No gaps listed."
          />
        </div>
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Todo</p>
          <TextList
            items={todo}
            emptyLabel="No todo items listed."
          />
        </div>
      </div>
    </div>
  );
}

function ReadinessSummaryView({ data }: { data: TJsonObject }) {
  const summary = asObject(data.summary);
  const categories = asObject(data.categories);
  const fundingRecommendations = asArray(data.funding_recommendations).map(asObject).filter(Boolean) as TJsonObject[];
  const todoCreationResults = asObject(data.todo_creation_results);
  const llmUsage = asObject(data.llm_usage);
  const embeddingUsage = asObject(data.usage_stats);
  const warnings = asArray(data.evaluation_warnings);

  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-md border border-border bg-card p-3">
          <p className="text-xs font-medium text-muted-foreground">Evaluation</p>
          <p className="mt-1 text-sm font-semibold text-foreground">{asString(data.evaluation_type) || "Unknown"}</p>
        </div>
        <div className="rounded-md border border-border bg-card p-3">
          <p className="text-xs font-medium text-muted-foreground">Categories</p>
          <p className="mt-1 text-sm font-semibold text-foreground">
            {formatNumber(summary?.total_categories ?? Object.keys(categories ?? {}).length, 0)}
          </p>
        </div>
        <div className="rounded-md border border-border bg-card p-3">
          <p className="text-xs font-medium text-muted-foreground">Average score</p>
          <p className="mt-1 text-sm font-semibold text-foreground">{formatNumber(summary?.average_score, 1)}</p>
        </div>
        <div className="rounded-md border border-border bg-card p-3">
          <p className="text-xs font-medium text-muted-foreground">Todo tasks</p>
          <p className="mt-1 text-sm font-semibold text-foreground">{formatNumber(data.todo_tasks_created, 0)}</p>
        </div>
      </div>

      {categories && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Category assessments</h3>
          {Object.entries(categories).map(([categoryName, categoryValue]) => {
            const category = asObject(categoryValue);
            const maturity = asObject(category?.maturity);
            const performance = asObject(category?.performance);
            const categoryTodo = asArray(category?.todo);

            return (
              <section
                key={categoryName}
                className="space-y-3 rounded-md border border-border bg-muted/30 p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-base font-semibold text-foreground">{categoryName}</h4>
                  <div className="flex flex-wrap gap-2">
                    {maturity?.level !== undefined && (
                      <span className="rounded-full bg-background px-2 py-1 text-xs font-medium text-foreground">
                        Maturity: {asString(maturity.level)}
                      </span>
                    )}
                    {performance?.level !== undefined && (
                      <span className="rounded-full bg-background px-2 py-1 text-xs font-medium text-foreground">
                        Performance: {asString(performance.level)}
                      </span>
                    )}
                    {performance?.score !== undefined && (
                      <span className="rounded-full bg-background px-2 py-1 text-xs font-medium text-foreground">
                        Score {formatNumber(performance.score, 0)}
                      </span>
                    )}
                  </div>
                </div>

                <AssessmentBlock
                  title="Maturity"
                  assessment={maturity}
                />
                <AssessmentBlock
                  title="Performance"
                  assessment={performance}
                />

                {categoryTodo.length > 0 && (
                  <details>
                    <summary className="cursor-pointer text-sm font-medium text-foreground">
                      Combined category todo ({categoryTodo.length})
                    </summary>
                    <div className="mt-3">
                      <TextList
                        items={categoryTodo}
                        emptyLabel="No todo items listed."
                      />
                    </div>
                  </details>
                )}
              </section>
            );
          })}
        </div>
      )}

      {fundingRecommendations.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">Funding recommendations</h3>
          <div className="grid gap-3 lg:grid-cols-2">
            {fundingRecommendations.map((recommendation, index) => (
              <div
                key={`${asString(recommendation.funding_type)}-${index}`}
                className="rounded-md border border-border bg-card p-3"
              >
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold text-foreground">
                    {formatFileType(asString(recommendation.funding_type))}
                  </h4>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
                    {asString(recommendation.suitability_level)} · {formatNumber(recommendation.suitability_score, 0)}
                  </span>
                </div>
                <p className="text-sm leading-6 text-foreground">{asString(recommendation.reasoning)}</p>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Strengths
                    </p>
                    <TextList
                      items={asArray(recommendation.key_strengths)}
                      emptyLabel="No strengths listed."
                    />
                  </div>
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Concerns
                    </p>
                    <TextList
                      items={asArray(recommendation.key_concerns)}
                      emptyLabel="No concerns listed."
                    />
                  </div>
                </div>
                {recommendation.assessment !== undefined && (
                  <p className="mt-3 rounded-md bg-muted p-2 text-xs leading-5 text-muted-foreground">
                    {asString(recommendation.assessment)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {todoCreationResults && (
        <details>
          <summary className="cursor-pointer text-sm font-semibold text-foreground">Created todo tasks</summary>
          <div className="mt-3 space-y-3">
            {Object.entries(todoCreationResults).map(([categoryName, tasks]) => {
              const taskList = asArray(tasks).map(asObject).filter(Boolean) as TJsonObject[];
              return (
                <div
                  key={categoryName}
                  className="rounded-md border border-border bg-card p-3"
                >
                  <p className="mb-2 text-sm font-semibold text-foreground">
                    {categoryName} ({taskList.length})
                  </p>
                  <div className="space-y-2">
                    {taskList.map((task, index) => {
                      const taskContent = splitTaskContent(asString(task.content));
                      return (
                        <div
                          key={`${asString(task.id)}-${index}`}
                          className="rounded-md border border-border bg-background p-2"
                        >
                          <p className="text-sm font-medium text-foreground">{taskContent.title}</p>
                          {taskContent.description && (
                            <p className="mt-1 text-xs leading-5 text-muted-foreground">{taskContent.description}</p>
                          )}
                          {taskContent.outcome && (
                            <p className="mt-1 text-xs leading-5 text-muted-foreground">
                              Expected: {taskContent.outcome}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </details>
      )}

      <details>
        <summary className="cursor-pointer text-sm font-semibold text-foreground">Run metadata and usage</summary>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <div className="rounded-md border border-border bg-card p-3 text-sm text-foreground">
            <p>
              <span className="font-medium">Run ID:</span> {asString(data.run_id) || "N/A"}
            </p>
            <p className="mt-1">
              <span className="font-medium">Dataset:</span> {asString(data.dataset_name) || "N/A"}
            </p>
            <p className="mt-1">
              <span className="font-medium">Evaluation date:</span> {formatDate(asString(data.evaluation_date))}
            </p>
            <p className="mt-1">
              <span className="font-medium">Total cost:</span> ${formatNumber(data.total_cost, 6)}
            </p>
          </div>
          <div className="rounded-md border border-border bg-card p-3 text-sm text-foreground">
            <p className="font-medium">Model usage</p>
            {[...Object.values(llmUsage ?? {}), ...Object.values(embeddingUsage ?? {})].map((usage, index) => {
              const usageObject = asObject(usage);
              return (
                <p
                  key={`${asString(usageObject?.llm_model)}-${index}`}
                  className="mt-1 text-xs leading-5 text-muted-foreground"
                >
                  {asString(usageObject?.llm_model)}: {formatNumber(usageObject?.total_requests, 0)} requests,{" "}
                  {formatNumber(usageObject?.total_tokens, 0)} tokens, ${formatNumber(usageObject?.total_cost, 6)}
                </p>
              );
            })}
          </div>
        </div>
        {warnings.length > 0 && (
          <div className="mt-3 rounded-md border border-border bg-card p-3">
            <p className="mb-2 text-sm font-medium text-foreground">Warnings</p>
            <TextList
              items={warnings}
              emptyLabel="No warnings."
            />
          </div>
        )}
      </details>

      <details>
        <summary className="cursor-pointer text-sm font-semibold text-foreground">Raw summary JSON</summary>
        <pre className="mt-2 max-h-96 overflow-auto rounded bg-muted p-3 text-xs text-foreground">
          {JSON.stringify(data, null, 2)}
        </pre>
      </details>
    </div>
  );
}

export function ReviewsPage() {
  const { toast } = useToast();
  const [query, setQuery] = useState("");
  const [companies, setCompanies] = useState<TReviewerCompany[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<TReviewerCompany | null>(null);
  const [activeKind, setActiveKind] = useState<TReviewKind>("investor-readiness");
  const [sourceRecords, setSourceRecords] = useState<Record<TReviewKind, TSourceRecord[]>>({
    "investor-readiness": [],
    "fundraiser-summary": [],
    "investor-summary": [],
  });
  const [selectedSourceIds, setSelectedSourceIds] = useState<Record<TReviewKind, string | null>>({
    "investor-readiness": null,
    "fundraiser-summary": null,
    "investor-summary": null,
  });
  const [documents, setDocuments] = useState<TReviewerDocument[]>([]);
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [form, setForm] = useState<TReviewFormValues>(emptyForm);
  const [searching, setSearching] = useState(false);
  const [loadingSources, setLoadingSources] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loadingReview, setLoadingReview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [documentError, setDocumentError] = useState<string | null>(null);

  const activeSources = sourceRecords[activeKind];
  const activeSourceId = selectedSourceIds[activeKind];
  const activeSource = useMemo(
    () => activeSources.find((source) => getSourceId(activeKind, source) === activeSourceId) ?? null,
    [activeKind, activeSourceId, activeSources]
  );
  const sourceSummary = getSourceSummary(activeKind, activeSource);
  const sourceJson = getSourceJson(activeKind, activeSource);
  const parsedReadinessSummary = useMemo(
    () => (activeKind === "investor-readiness" && sourceSummary ? parseJsonObject(sourceSummary) : null),
    [activeKind, sourceSummary]
  );

  const searchCompanies = async () => {
    setSearching(true);
    setError(null);
    const { data, error: searchError } = await reviewerService.searchFundraiserCompanies(query);
    setSearching(false);

    if (searchError) {
      const errorMessage = await extractErrorMessage(searchError);
      setCompanies([]);
      setError(errorMessage);
      return;
    }

    setCompanies(data?.data ?? []);
  };

  const loadCompanyReviewSources = async (company: TReviewerCompany) => {
    setLoadingSources(true);
    setLoadingDocuments(true);
    setError(null);
    setDocumentError(null);
    setReviewId(null);
    setForm(emptyForm);

    const [readiness, fundraiserSummaries, investorSummaries, fundraiserDocuments] = await Promise.all([
      reviewerService.getCompanyReadiness(company.id),
      reviewerService.getFundraiserSummaries(company.id),
      reviewerService.getInvestorSummaries(company.id),
      reviewerService.getFundraiserDocuments(company.id),
    ]);

    setLoadingSources(false);
    setLoadingDocuments(false);

    const firstError = readiness.error ?? fundraiserSummaries.error ?? investorSummaries.error;
    if (firstError) {
      setError(await extractErrorMessage(firstError));
    }

    if (fundraiserDocuments.error) {
      setDocuments([]);
      setDocumentError(await extractErrorMessage(fundraiserDocuments.error));
    } else {
      setDocuments(fundraiserDocuments.data?.data.files ?? []);
    }

    const nextSources = {
      "investor-readiness": readiness.data?.data ?? [],
      "fundraiser-summary": fundraiserSummaries.data?.data ?? [],
      "investor-summary": investorSummaries.data?.data ?? [],
    };

    setSourceRecords(nextSources);
    setSelectedSourceIds({
      "investor-readiness": nextSources["investor-readiness"][0]?.id ?? null,
      "fundraiser-summary": nextSources["fundraiser-summary"][0]?.fundraiser_summary_for_investors_id ?? null,
      "investor-summary": nextSources["investor-summary"][0]?.investor_summary_for_fundraisers_id ?? null,
    });
  };

  const downloadDocument = async (document: TReviewerDocument) => {
    setDownloadingDocumentId(document.id);
    setDocumentError(null);

    const response = await reviewerService.downloadFundraiserDocument(document.id);
    setDownloadingDocumentId(null);

    if (response.error || !response.data) {
      const errorMessage = response.error ? await extractErrorMessage(response.error) : "Failed to download document";
      setDocumentError(errorMessage);
      toast({ variant: "destructive", title: "Failed to download document", description: errorMessage });
      return;
    }

    const blob =
      response.data.blob.type || !document.content_type
        ? response.data.blob
        : new Blob([response.data.blob], { type: document.content_type });
    triggerBlobDownload(blob, response.data.filename ?? document.original_filename);
  };

  const selectCompany = (company: TReviewerCompany) => {
    setSelectedCompany(company);
    void loadCompanyReviewSources(company);
  };

  useEffect(() => {
    const loadReview = async () => {
      if (!activeSourceId) {
        setReviewId(null);
        setForm(emptyForm);
        return;
      }

      setLoadingReview(true);
      setError(null);

      const response =
        activeKind === "investor-readiness"
          ? await reviewerService.getInvestorReadinessReviews(activeSourceId)
          : activeKind === "fundraiser-summary"
            ? await reviewerService.getFundraiserSummaryReviews(activeSourceId)
            : await reviewerService.getInvestorSummaryReviews(activeSourceId);

      setLoadingReview(false);

      if (response.error) {
        setError(await extractErrorMessage(response.error));
        setReviewId(null);
        setForm(emptyForm);
        return;
      }

      const review = response.data?.data[0] ?? null;
      setReviewId(review?.id ?? null);
      setForm({
        positive_critique_of_ai: review?.positive_critique_of_ai ?? "",
        negative_critique_of_ai: review?.negative_critique_of_ai ?? "",
        reviewer_summary: getReviewSummary(review),
      });
    };

    void loadReview();
  }, [activeKind, activeSourceId]);

  const saveReview = async () => {
    if (!activeSourceId) return;

    setSaving(true);
    setError(null);

    const response =
      activeKind === "investor-readiness"
        ? await reviewerService.saveInvestorReadinessReview(activeSourceId, reviewId, form)
        : activeKind === "fundraiser-summary"
          ? await reviewerService.saveFundraiserSummaryReview(activeSourceId, reviewId, form)
          : await reviewerService.saveInvestorSummaryReview(activeSourceId, reviewId, form);

    setSaving(false);

    if (response.error) {
      const errorMessage = await extractErrorMessage(response.error);
      setError(errorMessage);
      toast({ variant: "destructive", title: "Failed to save review", description: errorMessage });
      return;
    }

    setReviewId(response.data?.data.id ?? reviewId);
    toast({ variant: "success", title: reviewId ? "Review updated" : "Review created" });
  };

  return (
    <div className="h-full overflow-y-auto bg-card p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Reviews</h1>
          <p className="mt-2 text-sm text-muted-foreground">Review AI-generated summaries for fundraiser companies.</p>
        </div>

        <div className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Company Search</CardTitle>
                <CardDescription>Find a fundraiser company</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") void searchCompanies();
                    }}
                    placeholder="Company name"
                  />
                  <Button
                    type="button"
                    onClick={() => void searchCompanies()}
                    disabled={searching}
                  >
                    {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="space-y-2">
                  {companies.map((company) => {
                    const isSelected = selectedCompany?.id === company.id;
                    return (
                      <button
                        key={company.id}
                        type="button"
                        onClick={() => selectCompany(company)}
                        className={`w-full rounded-md border p-3 text-left text-sm transition-colors ${
                          isSelected
                            ? "border-primary bg-primary/10 text-foreground"
                            : "border-border bg-background text-foreground hover:bg-muted"
                        }`}
                      >
                        <span className="block font-medium">{company.name}</span>
                        <span className="mt-1 block text-xs text-muted-foreground">
                          {company.onboarding_completed ? "Onboarding complete" : "Onboarding incomplete"}
                        </span>
                      </button>
                    );
                  })}
                  {!searching && companies.length === 0 && (
                    <p className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
                      No fundraiser companies loaded.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">{error}</div>}

            {selectedCompany && (
              <Card>
                <CardHeader>
                  <CardTitle>Fundraiser Documents</CardTitle>
                  <CardDescription>Documents uploaded during onboarding or later company chats</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {documentError && <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">{documentError}</div>}

                  {loadingDocuments ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : documents.length === 0 ? (
                    <p className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
                      No documents found for this fundraiser company.
                    </p>
                  ) : (
                    <div className="divide-y divide-border rounded-md border border-border bg-background">
                      {documents.map((document) => {
                        const isDownloading = downloadingDocumentId === document.id;
                        return (
                          <div
                            key={document.id}
                            className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between"
                          >
                            <div className="flex min-w-0 items-start gap-3">
                              <FileText className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-foreground">
                                  {document.original_filename}
                                </p>
                                <p className="mt-1 text-xs text-muted-foreground">
                                  {formatFileType(document.file_type)} · {formatFileSize(document.file_size)} ·{" "}
                                  {formatDate(document.created_at)}
                                </p>
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => void downloadDocument(document)}
                              disabled={isDownloading}
                              className="shrink-0"
                            >
                              {isDownloading ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="mr-2 h-4 w-4" />
                              )}
                              Download
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <div className="grid gap-3 md:grid-cols-3">
              {REVIEW_TYPES.map((reviewType) => (
                <button
                  key={reviewType.id}
                  type="button"
                  onClick={() => setActiveKind(reviewType.id)}
                  className={`rounded-md border p-4 text-left transition-colors ${
                    activeKind === reviewType.id
                      ? "border-primary bg-primary/10"
                      : "border-border bg-background hover:bg-muted"
                  }`}
                >
                  <span className="block text-sm font-semibold text-foreground">{reviewType.label}</span>
                  <span className="mt-1 block text-xs text-muted-foreground">{reviewType.description}</span>
                  <span className="mt-3 block text-xs font-medium text-muted-foreground">
                    {sourceRecords[reviewType.id].length} source records
                  </span>
                </button>
              ))}
            </div>

            <Card>
              <CardHeader>
                <CardTitle>{REVIEW_TYPES.find((reviewType) => reviewType.id === activeKind)?.label}</CardTitle>
                <CardDescription>
                  {selectedCompany ? selectedCompany.name : "Select a fundraiser company to load review material"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {loadingSources ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : activeSources.length === 0 ? (
                  <p className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
                    No source records found for this review type.
                  </p>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-2">
                      {activeSources.map((source, index) => {
                        const sourceId = getSourceId(activeKind, source);
                        return (
                          <Button
                            key={sourceId}
                            type="button"
                            variant={sourceId === activeSourceId ? "default" : "outline"}
                            size="sm"
                            onClick={() =>
                              setSelectedSourceIds((current) => ({
                                ...current,
                                [activeKind]: sourceId,
                              }))
                            }
                          >
                            Record {index + 1}
                          </Button>
                        );
                      })}
                    </div>

                    <div className="rounded-md border border-border bg-background p-4">
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-foreground">AI Summary</p>
                        <p className="text-xs text-muted-foreground">{formatDate(getSourceDate(activeSource))}</p>
                      </div>
                      {parsedReadinessSummary ? (
                        <ReadinessSummaryView data={parsedReadinessSummary} />
                      ) : sourceSummary ? (
                        <p className="whitespace-pre-wrap text-sm leading-6 text-foreground">{sourceSummary}</p>
                      ) : (
                        <p className="text-sm text-muted-foreground">No summary text returned.</p>
                      )}
                      {sourceJson && (
                        <details className="mt-4">
                          <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                            Source data
                          </summary>
                          <pre className="mt-2 max-h-80 overflow-auto rounded bg-muted p-3 text-xs text-foreground">
                            {JSON.stringify(sourceJson, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>

                    <div className="grid gap-4">
                      <label className="space-y-2">
                        <span className="text-sm font-medium text-foreground">Positive comments</span>
                        <Textarea
                          value={form.positive_critique_of_ai ?? ""}
                          minHeight={120}
                          maxHeight={260}
                          onChange={(event) =>
                            setForm((current) => ({
                              ...current,
                              positive_critique_of_ai: event.target.value,
                            }))
                          }
                        />
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-medium text-foreground">Negative comments</span>
                        <Textarea
                          value={form.negative_critique_of_ai ?? ""}
                          minHeight={120}
                          maxHeight={260}
                          onChange={(event) =>
                            setForm((current) => ({
                              ...current,
                              negative_critique_of_ai: event.target.value,
                            }))
                          }
                        />
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-medium text-foreground">Reviewer summary</span>
                        <Textarea
                          value={form.reviewer_summary ?? ""}
                          minHeight={160}
                          maxHeight={360}
                          onChange={(event) =>
                            setForm((current) => ({
                              ...current,
                              reviewer_summary: event.target.value,
                            }))
                          }
                        />
                      </label>
                    </div>

                    <div className="flex items-center justify-between border-t border-border pt-4">
                      <span className="text-sm text-muted-foreground">
                        {loadingReview
                          ? "Loading review..."
                          : reviewId
                            ? "Editing existing review"
                            : "Creating new review"}
                      </span>
                      <Button
                        type="button"
                        onClick={() => void saveReview()}
                        disabled={saving || loadingReview || !activeSourceId}
                      >
                        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {reviewId ? "Update review" : "Create review"}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
